<template>
  <div class="py-4">
    <v-card class="mx-auto" max-width="1400">
      <v-toolbar color="primary" dark>
        <v-toolbar-title>이미지 업로더 유틸</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadImageList">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <!-- 이미지 업로드 섹션 -->
        <v-card outlined class="mb-4">
          <v-card-title>
            <v-icon left>mdi-upload</v-icon>
            이미지 업로드
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="8">
                <v-file-input
                  v-model="uploadFile"
                  label="이미지 파일 선택"
                  accept="image/*"
                  prepend-icon="mdi-camera"
                  show-size
                  @change="handleFileChange"
                ></v-file-input>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="primary"
                  :disabled="!uploadFile || uploading"
                  :loading="uploading"
                  @click="uploadImage"
                  block
                >
                  <v-icon left>mdi-cloud-upload</v-icon>
                  업로드
                </v-btn>
              </v-col>
            </v-row>
            <v-alert v-if="uploadMessage" :type="uploadMessageType" dismissible class="mt-3">
              {{ uploadMessage }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- 이미지 리스트 섹션 -->
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-image-multiple</v-icon>
            업로드된 이미지 목록
          </v-card-title>
          <v-card-text>
            <v-row v-if="imageList.length === 0 && !loadingImages">
              <v-col cols="12" class="text-center py-8">
                <v-icon size="64" color="grey lighten-1">mdi-image-off</v-icon>
                <div class="mt-4 grey--text">등록된 이미지가 없습니다.</div>
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col
                v-for="(item, index) in imageList"
                :key="index"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card outlined class="image-card">
                  <v-img
                    :src="getImageUrl(item.thumb_path)"
                    :lazy-src="getImageUrl(item.thumb_path)"
                    aspect-ratio="1"
                    class="thumbnail-image"
                    @error="handleImageError"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-text class="pa-2">
                    <div class="text-caption grey--text mb-1">
                      등록일: {{ formatDate(item.create_dt) }}
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        :value="getImageUrl(item.thumb_path)"
                        readonly
                        dense
                        hide-details
                        class="mr-2"
                        style="font-size: 12px;"
                      ></v-text-field>
                      <v-btn
                        icon
                        small
                        color="primary"
                        @click="copyToClipboard(getImageUrl(item.thumb_path))"
                      >
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row v-if="loadingImages">
              <v-col cols="12" class="text-center py-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { FileUpload_Async } from '../util/fileutil';
import config from '../config/config';

export default {
  name: 'ImageUploaderUtil',
  
  data: () => ({
    uploadFile: null,
    uploading: false,
    uploadMessage: '',
    uploadMessageType: 'success',
    imageList: [],
    loadingImages: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success'
  }),

  created() {
    this.loadImageList();
  },

  methods: {
    ...mapActions([
      'INSERT_UTIL_IMG_S3',
      'GET_UTIL_IMG_S3_LIST'
    ]),

    handleFileChange(file) {
      if (file) {
        // 파일 크기 체크 (10MB 제한)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          this.uploadMessage = '파일 크기는 10MB를 초과할 수 없습니다.';
          this.uploadMessageType = 'error';
          this.uploadFile = null;
        } else {
          this.uploadMessage = '';
        }
      }
    },

    async uploadImage() {
      if (!this.uploadFile) {
        this.showSnackbar('파일을 선택해주세요.', 'error');
        return;
      }

      this.uploading = true;
      this.uploadMessage = '';

      try {
        // 1. S3에 이미지 업로드
        const imagePath = `util/images/${this.Utill_CreateNewImageName(this.uploadFile.name)}`;
        const uploadResult = await FileUpload_Async(this.uploadFile, imagePath);

        if (!uploadResult) {
          throw new Error('S3 업로드 실패');
        }

        // 2. 이미지 URL 생성
        const imageUrl = config.s3_ImgQuizBucket + imagePath;   // CloudFront 미사용 — 직접 S3 URL 로 저장

        // 3. DB에 저장 (thumb_path 필드에 이미지 URL 저장)
        await this.INSERT_UTIL_IMG_S3({ thumb_path: imageUrl });

        this.uploadMessage = '이미지가 성공적으로 업로드되었습니다.';
        this.uploadMessageType = 'success';
        this.uploadFile = null;

        // 4. 리스트 새로고침
        await this.loadImageList();
        this.showSnackbar('이미지 업로드 완료', 'success');
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
        this.uploadMessage = '이미지 업로드에 실패했습니다.';
        this.uploadMessageType = 'error';
        this.showSnackbar('이미지 업로드 실패', 'error');
      } finally {
        this.uploading = false;
      }
    },

    async loadImageList() {
      this.loadingImages = true;
      try {
        const body_data = await this.GET_UTIL_IMG_S3_LIST({ count: 20 });
        console.log('📥 전체 응답 데이터:', JSON.stringify(body_data, null, 2));
        this.imageList = body_data.list || body_data.response_data?.list || [];
        console.log('📋 이미지 리스트:', JSON.stringify(this.imageList, null, 2));
        
        // 각 이미지 항목의 URL 확인
        this.imageList.forEach((item, index) => {
          const imageUrl = this.getImageUrl(item.thumb_path);
          console.log(`🖼️ 이미지 ${index + 1}:`, {
            원본데이터: item,
            thumb_path: item.thumb_path,
            최종URL: imageUrl
          });
        });
      } catch (error) {
        console.error('이미지 리스트 로드 실패:', error);
        this.showSnackbar('이미지 리스트를 불러오는데 실패했습니다.', 'error');
      } finally {
        this.loadingImages = false;
      }
    },

    getImageUrl(url) {
      if (!url) {
        console.warn('⚠️ 이미지 URL이 없습니다:', url);
        return require('../assets/no_image.jpg');
      }
      
      // CloudFront 미사용 — 직접 S3 URL(content-filecenter)로 통일
      const S3_BASE = config.s3_ImgQuizBucket;   // https://content-filecenter.s3.ap-northeast-2.amazonaws.com/

      // 과거 CloudFront URL 로 저장된 값은 도메인만 S3 로 치환 (호환)
      const cf = url.match(/^https?:\/\/d1r6v24dyewnnj\.cloudfront\.net\/(.*)$/);
      if (cf) return S3_BASE + cf[1];

      // 이미 전체 URL(S3 등)이면 그대로 사용
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      // 상대 경로(raw key)면 S3 base 를 붙인다
      return S3_BASE + url;
    },

    handleImageError(event) {
      console.error('이미지 로드 실패:', event);
      event.target.src = require('../assets/no_image.jpg');
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.showSnackbar('링크가 클립보드에 복사되었습니다.', 'success');
      } catch (error) {
        console.error('클립보드 복사 실패:', error);
        // Fallback: 텍스트 영역 사용
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.showSnackbar('링크가 클립보드에 복사되었습니다.', 'success');
        } catch (err) {
          this.showSnackbar('클립보드 복사에 실패했습니다.', 'error');
        }
        document.body.removeChild(textArea);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    Utill_CreateNewImageName(originalFileName) {
      // 확장자 추출
      const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));
      // 랜덤 문자열 생성 (16자)
      const randomString = Math.random().toString(36).substring(2, 18);
      // 타임스탬프 추가
      const timestamp = Date.now();
      // 새 파일 이름 생성
      return `${timestamp}_${randomString}${fileExtension}`;
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.image-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.image-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.thumbnail-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  background-color: #f5f5f5;
}
</style>

